import { useEffect, useState } from 'react';
import { Globe, CheckCircle, XCircle, Loader, AlertCircle } from 'lucide-react';
import { api } from '../services/api';

interface CrawlProgressProps {
  jobId: string;
  onComplete: () => void;
  onError?: (error: string) => void;
}

export default function CrawlProgress({ jobId, onComplete, onError }: CrawlProgressProps) {
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let interval: NodeJS.Timeout;

    const checkStatus = async () => {
      try {
        const data = await api.getCrawlStatus(jobId);
        setStatus(data);

        if (data.status === 'completed') {
          clearInterval(interval);
          setTimeout(() => onComplete(), 1500);
        } else if (data.status === 'failed') {
          clearInterval(interval);
          const errorMsg = data.error || 'Crawl failed';
          setError(errorMsg);
          if (onError) onError(errorMsg);
        }
      } catch (err: any) {
        console.error('Error checking crawl status:', err);
        setError('Failed to check crawl status');
        if (onError) onError('Failed to check crawl status');
      } finally {
        setLoading(false);
      }
    };

    // Initial check
    checkStatus();

    // Poll every 2 seconds
    interval = setInterval(checkStatus, 2000);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [jobId, onComplete, onError]);

  const getStatusIcon = () => {
    if (loading || !status) {
      return <Loader className="w-12 h-12 text-black animate-spin" />;
    }

    switch (status.status) {
      case 'completed':
        return <CheckCircle className="w-12 h-12 text-green-600" />;
      case 'failed':
        return <XCircle className="w-12 h-12 text-red-600" />;
      case 'running':
        return <Globe className="w-12 h-12 text-black animate-pulse" />;
      default:
        return <Loader className="w-12 h-12 text-black animate-spin" />;
    }
  };

  const getStatusText = () => {
    if (loading || !status) {
      return 'Starting crawl...';
    }

    switch (status.status) {
      case 'completed':
        return 'Crawl completed!';
      case 'failed':
        return 'Crawl failed';
      case 'running':
        return 'Crawling your website...';
      default:
        return 'Processing...';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full p-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            {getStatusIcon()}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {getStatusText()}
          </h2>

          {error ? (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          ) : status && status.status === 'running' ? (
            <div className="space-y-4 mt-6">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Pages discovered:</span>
                <span className="font-semibold">{status.pages_discovered || 0}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Pages crawled:</span>
                <span className="font-semibold">{status.pages_crawled || 0}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Pages indexed:</span>
                <span className="font-semibold">{status.pages_indexed || 0}</span>
              </div>
            </div>
          ) : status && status.status === 'completed' ? (
            <div className="space-y-3 mt-6">
              <div className="flex items-center justify-center gap-2 text-green-600">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Successfully crawled {status.pages_crawled || 0} pages</span>
              </div>
              <p className="text-sm text-gray-600">Indexed {status.pages_indexed || 0} pages - Your AI is ready!</p>
            </div>
          ) : null}

          {status && status.status === 'running' && (
            <div className="mt-6">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-black to-black h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${status.pages_discovered > 0
                      ? Math.min((status.pages_crawled / status.pages_discovered) * 100, 100)
                      : 0}%`,
                  }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                This usually takes 2-5 minutes depending on your site size
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
